import React, { useContext, useState, useEffect } from "react";
import { UserContext } from "contexts/UserContext";
import { MapContext } from "contexts/MapContext";
import { makeStyles } from "@material-ui/core/styles";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import Chip from "@material-ui/core/Chip";
import UserDefault from "assets/icons/UserDefault.svg";
import { loadModules } from "esri-loader";
import { CircularProgress } from "@material-ui/core";

const useStyles = makeStyles({
  flex: {
    display: "flex",
    border: "none",
    boxShadow: "none",
    margin: "20px 20px 0 20px",
  },
  photo: {
    background: `url(${UserDefault})`,
    borderRadius: "50%",
    backgroundPosition: "center",
    border: "3px solid #22471B",
    width: "75px",
    height: "75px",
    backgroundRepeat: "no-repeat",
  },
  columnFlex: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "16px",
    marginTop: "20px",
  },
  name: {
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "24px",
    letterSpacing: "0.15px",
    textAlign: "left",
  },
  joinedDate: {
    fontFamily: "Open Sans",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    textAlign: "left",
    marginTop: "4px",
  },
  notLoggedInMsg: {
    color: "#4D4D4D",
    fontFamily: "Open Sans",
    fontWeight: "300",
    fontSize: "14px",
    lineHeight: "16.8px",
    letterSpacing: "0.25px",
    margin: "10px",
  },
  total: {
    fontFamily: "Open Sans",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "300",
    lineHeight: "17px",
    letterSpacing: "0.25px",
    textAlign: "left",
  },
  totalResult: {
    marginTop: "3px",
    fontFamily: "Open Sans",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "17px",
    letterSpacing: "0.25px",
    textAlign: "left",
  },
  option: {
    display: "flex",
    width: "97%",
    height: "40px",
    outline: "1px solid #EAEAEA",
    justifyContent: "space-between",
  },
  optionInnerElement: {
    fontFamily: "Open Sans",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    margin: "10px 0 10px 17px",
  },
  optionsFlex: {
    marginTop: "20px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
  },
  versionFlex: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "center",
    color: "#4D4D4D",
  },
  versionNumber: {
    fontFamily: "Open Sans",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "17px",
    letterSpacing: "0.25px",
    margin: "2px",
  },
  versionInfo: {
    fontFamily: "Open Sans",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "300",
    lineHeight: "17px",
    letterSpacing: "0.25px",
    margin: "2px",
  },
  footerContainer: {
    display: "flex",
    position: "fixed",
    left: "0",
    bottom: "3px",
    justifyContent: "center",
    width: "30%",
  },
  footer: {
    color: "#B52626",
    borderColor: "#B52626",
    width: "90%",
    height: "40px",
    fontFamily: "Open Sans",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "25px",
    letterSpacing: "0.75px",
    textAlign: "left",
    marginRight: "10px",
  },
  footerRoot: {
    borderRadius: "4px",
  },
  loading: {
    textAlign: "center",
  },
});

export default function ProfileDetails({}) {
  const { setSidebarSelection } = useContext(MapContext);
  const { userInfo } = useContext(UserContext);
  const [loading, setLoading] = useState(null);
  const [totalDistance, setTotalDistance] = useState(null);
  const [totalTrips, setTotalTrips] = useState(null);
  const classes = useStyles();

  useEffect(() => {
    if (userInfo) {
      (async () => {
        setLoading(true);
        await setTripHistoryInfo(userInfo);
        setLoading(false);
      })();
    }
  }, []);

  useEffect(() => {
    if (userInfo) {
      (async () => {
        setLoading(true);
        await setTripHistoryInfo(userInfo);
        setLoading(false);
      })();
    }
  }, [userInfo]);

  const setTripHistoryInfo = async (userInfo) => {
    const [esriRequest] = await loadModules(["esri/request"]);

    let res = await esriRequest(userInfo.tripHistoryUrl + "/query", {
      method: "post",
      query: {
        where: "1=1",
        outFields: "*",
        f: "json", // format
      },
    });

    setTotalTrips(res.data.features.length);
    setTotalDistance(getTripTotalLength(res.data.features));
  };

  const getTripTotalLength = (features) => {
    let distances = features.map((feature) => feature.attributes.TotalDistance);
    return distances
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0)
      .toFixed(1);
  };

  const getJoinedDate = ({ user }) => {
    return new Date(user.created).toLocaleString("default", {
      month: "long",
      year: "numeric",
      day: "numeric",
    });
  };

  const getThumbnail = ({ credential, user }) => {
    return `${credential.server}/sharing/rest/community/users/${credential.userId}/info/${user.thumbnail}?token=${credential.token}`;
  };

  const getOptions = () => {
    const options = [
      {
        title: "Recorded Trips",
        handler: () => {
          setSidebarSelection(["Recorded Trips"]);
        },
      },
      {
        title: "Favorites",
        handler: () => {
          setSidebarSelection(["Favorites"]);
        },
      },
    ];
    return (
      <div className={classes.optionsFlex}>
        {options.map((option) => {
          return (
            <div className={classes.option} onClick={option.handler}>
              <div className={classes.optionInnerElement}>{option.title}</div>
              <div className={classes.optionInnerElement}>
                <ChevronRightIcon color="#4D7343" />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {userInfo ? (
        !loading ? (
          <>
            <div className={classes.flex}>
              {userInfo && userInfo.user.thumbnail ? (
                <div
                  className={classes.photo}
                  style={{ backgroundImage: `url(${getThumbnail(userInfo)})` }}
                />
              ) : (
                <div className={classes.photo} />
              )}
              <div className={classes.columnFlex}>
                <div className={classes.name}>
                  {userInfo ? userInfo.user.fullName : ""}
                </div>
                <div className={classes.joinedDate}>
                  Joined {getJoinedDate(userInfo)}
                </div>
              </div>
            </div>
            <div className={classes.flex}>
              <div className={classes.columnFlex}>
                <div className={classes.total}>Total Length</div>
                <div className={classes.totalResult}>
                  {totalDistance ? `${totalDistance} mi` : "0 mi"}
                </div>
              </div>
              <div className={classes.columnFlex}>
                <div className={classes.total}>Trips</div>
                <div className={classes.totalResult}>
                  {totalTrips ? `${totalTrips}` : "0"}
                </div>
              </div>
            </div>
            {getOptions()}
            <div className={classes.versionFlex}>
              <div className={classes.versionInfo}>App Version</div>
              <div className={classes.versionNumber}>2.1</div>
            </div>

            <div className={classes.footerContainer}>
              <Chip
                variant="outlined"
                size="small"
                label="LOGOUT"
                clickable
                className={classes.footer}
                classes={{ root: classes.footerRoot }}
              />
            </div>
          </>
        ) : (
          <CircularProgress color="inherit" className={classes.loading} />
        )
      ) : (
        <div className={classes.notLoggedInMsg}>
          Please login to view Profile information. If you do not have an
          account, please sign up for one{" "}
          <a
            href="https://ohid.ohio.gov/wps/portal/gov/ohid/create-account/"
            target="_blank"
          >
            here
          </a>
          .
        </div>
      )}
    </>
  );
}
